'use client';

import { createClient } from '@/lib/supabase/client';

export interface WebsiteContent {
  id: string;
  section_key: string;
  section_name: string;
  page: string;
  content_type: string;
  content_value: string;
  description?: string;
  updated_at: string;
}

export interface MediaAsset {
  id: string;
  asset_key: string;
  asset_name: string;
  asset_type: string;
  page: string;
  current_url: string;
  alt_text: string;
  description?: string;
  updated_at: string;
}

export interface PageSection {
  id: string;
  section_key: string;
  section_name: string;
  page: string;
  section_group: string;
  content_type: string;
  content_value: string;
  description?: string;
  is_visible: boolean;
  sort_order: number;
  updated_at: string;
}

export interface ApplicationRecord {
  id: string;
  full_name?: string;
  organization_name?: string;
  contact_name?: string;
  email?: string;
  status: string;
  [key: string]: any;
}

// ─── Resource Hub Types ────────────────────────────────────────────────────────
export interface ManagedResource {
  id: string;
  title: string;
  description: string;
  category: string;
  type: string;
  image_url: string;
  alt_text: string;
  file_url: string;
  file_size: string;
  duration: string;
  pages?: number | null;
  languages: string[];
  downloads: number;
  rating: number;
  is_visible: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface ManagedToolkit {
  id: string;
  title: string;
  description: string;
  image_url: string;
  alt_text: string;
  user_type: string;
  includes: string[];
  is_visible: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface ManagedVideo {
  id: string;
  title: string;
  description: string;
  thumbnail_url: string;
  alt_text: string;
  video_url: string;
  duration: string;
  views: number;
  category: string;
  is_visible: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

// ─── Media Center Types ────────────────────────────────────────────────────────
export interface ManagedPressRelease {
  id: string;
  title: string;
  date: string;
  category: string;
  excerpt: string;
  full_content: string;
  read_time: string;
  pdf_url: string;
  is_visible: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface ManagedMediaCoverage {
  id: string;
  outlet: string;
  title: string;
  date: string;
  type: string;
  thumbnail_url: string;
  alt_text: string;
  url: string;
  reach: string;
  is_visible: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface ManagedPressKitItem {
  id: string;
  title: string;
  description: string;
  file_size: string;
  file_type: string;
  download_url: string;
  icon: string;
  category: string;
  is_visible: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export const adminContentService = {
  // ─── PAGE SECTIONS ──────────────────────────────────────────────────
  async getPageSections(page?: string): Promise<PageSection[]> {
    const supabase = createClient();
    let query = supabase
      .from('page_sections')
      .select('*')
      .order('sort_order', { ascending: true });
    if (page) query = query.eq('page', page);
    const { data, error } = await query;
    if (error) throw new Error(error.message);
    return (data as PageSection[]) || [];
  },

  async updatePageSection(id: string, content_value: string): Promise<void> {
    const supabase = createClient();
    const { error } = await supabase
      .from('page_sections')
      .update({ content_value, updated_at: new Date().toISOString() })
      .eq('id', id);
    if (error) throw new Error(error.message);
  },

  async togglePageSectionVisibility(id: string, is_visible: boolean): Promise<void> {
    const supabase = createClient();
    const { error } = await supabase
      .from('page_sections')
      .update({ is_visible, updated_at: new Date().toISOString() })
      .eq('id', id);
    if (error) throw new Error(error.message);
  },

  async upsertPageSection(section_key: string, content_value: string, section_name: string, page: string, section_group: string, content_type: string = 'text'): Promise<void> {
    const supabase = createClient();
    const { error } = await supabase
      .from('page_sections')
      .upsert({ section_key, section_name, page, section_group, content_type, content_value, updated_at: new Date().toISOString() }, { onConflict: 'section_key' });
    if (error) throw new Error(error.message);
  },

  // ─── WEBSITE CONTENT ───────────────────────────────────────────────
  async getAllContent(): Promise<WebsiteContent[]> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('website_content')
      .select('*')
      .order('page', { ascending: true });
    if (error) throw new Error(error.message);
    return (data as WebsiteContent[]) || [];
  },

  async updateContent(id: string, content_value: string): Promise<void> {
    const supabase = createClient();
    const { error } = await supabase
      .from('website_content')
      .update({ content_value, updated_at: new Date().toISOString() })
      .eq('id', id);
    if (error) throw new Error(error.message);
  },

  async upsertContent(section_key: string, content_value: string, section_name: string, page: string, content_type: string = 'text'): Promise<void> {
    const supabase = createClient();
    const { error } = await supabase
      .from('website_content')
      .upsert({ section_key, section_name, page, content_type, content_value, updated_at: new Date().toISOString() }, { onConflict: 'section_key' });
    if (error) throw new Error(error.message);
  },

  // ─── MEDIA ASSETS ──────────────────────────────────────────────────
  async getAllMediaAssets(): Promise<MediaAsset[]> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('media_assets')
      .select('*')
      .order('page', { ascending: true });
    if (error) throw new Error(error.message);
    return (data as MediaAsset[]) || [];
  },

  async updateMediaAsset(id: string, updates: Partial<Pick<MediaAsset, 'current_url' | 'alt_text'>>): Promise<void> {
    const supabase = createClient();
    const { error } = await supabase
      .from('media_assets')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', id);
    if (error) throw new Error(error.message);
  },

  // ─── MANAGED RESOURCES ─────────────────────────────────────────────
  async getResources(): Promise<ManagedResource[]> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('managed_resources')
      .select('*')
      .order('sort_order', { ascending: true });
    if (error) throw new Error(error.message);
    return (data as ManagedResource[]) || [];
  },

  async createResource(resource: Omit<ManagedResource, 'id' | 'created_at' | 'updated_at'>): Promise<ManagedResource> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('managed_resources')
      .insert([{ ...resource, updated_at: new Date().toISOString() }])
      .select()
      .single();
    if (error) throw new Error(error.message);
    return data as ManagedResource;
  },

  async updateResource(id: string, updates: Partial<ManagedResource>): Promise<void> {
    const supabase = createClient();
    const { error } = await supabase
      .from('managed_resources')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', id);
    if (error) throw new Error(error.message);
  },

  async deleteResource(id: string): Promise<void> {
    const supabase = createClient();
    const { error } = await supabase.from('managed_resources').delete().eq('id', id);
    if (error) throw new Error(error.message);
  },

  // ─── MANAGED TOOLKITS ──────────────────────────────────────────────
  async getToolkits(): Promise<ManagedToolkit[]> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('managed_toolkits')
      .select('*')
      .order('sort_order', { ascending: true });
    if (error) throw new Error(error.message);
    return (data as ManagedToolkit[]) || [];
  },

  async createToolkit(toolkit: Omit<ManagedToolkit, 'id' | 'created_at' | 'updated_at'>): Promise<ManagedToolkit> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('managed_toolkits')
      .insert([{ ...toolkit, updated_at: new Date().toISOString() }])
      .select()
      .single();
    if (error) throw new Error(error.message);
    return data as ManagedToolkit;
  },

  async updateToolkit(id: string, updates: Partial<ManagedToolkit>): Promise<void> {
    const supabase = createClient();
    const { error } = await supabase
      .from('managed_toolkits')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', id);
    if (error) throw new Error(error.message);
  },

  async deleteToolkit(id: string): Promise<void> {
    const supabase = createClient();
    const { error } = await supabase.from('managed_toolkits').delete().eq('id', id);
    if (error) throw new Error(error.message);
  },

  // ─── MANAGED VIDEOS ────────────────────────────────────────────────
  async getVideos(): Promise<ManagedVideo[]> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('managed_videos')
      .select('*')
      .order('sort_order', { ascending: true });
    if (error) throw new Error(error.message);
    return (data as ManagedVideo[]) || [];
  },

  async createVideo(video: Omit<ManagedVideo, 'id' | 'created_at' | 'updated_at'>): Promise<ManagedVideo> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('managed_videos')
      .insert([{ ...video, updated_at: new Date().toISOString() }])
      .select()
      .single();
    if (error) throw new Error(error.message);
    return data as ManagedVideo;
  },

  async updateVideo(id: string, updates: Partial<ManagedVideo>): Promise<void> {
    const supabase = createClient();
    const { error } = await supabase
      .from('managed_videos')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', id);
    if (error) throw new Error(error.message);
  },

  async deleteVideo(id: string): Promise<void> {
    const supabase = createClient();
    const { error } = await supabase.from('managed_videos').delete().eq('id', id);
    if (error) throw new Error(error.message);
  },

  // ─── MANAGED PRESS RELEASES ────────────────────────────────────────
  async getPressReleases(): Promise<ManagedPressRelease[]> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('managed_press_releases')
      .select('*')
      .order('sort_order', { ascending: true });
    if (error) throw new Error(error.message);
    return (data as ManagedPressRelease[]) || [];
  },

  async createPressRelease(pr: Omit<ManagedPressRelease, 'id' | 'created_at' | 'updated_at'>): Promise<ManagedPressRelease> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('managed_press_releases')
      .insert([{ ...pr, updated_at: new Date().toISOString() }])
      .select()
      .single();
    if (error) throw new Error(error.message);
    return data as ManagedPressRelease;
  },

  async updatePressRelease(id: string, updates: Partial<ManagedPressRelease>): Promise<void> {
    const supabase = createClient();
    const { error } = await supabase
      .from('managed_press_releases')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', id);
    if (error) throw new Error(error.message);
  },

  async deletePressRelease(id: string): Promise<void> {
    const supabase = createClient();
    const { error } = await supabase.from('managed_press_releases').delete().eq('id', id);
    if (error) throw new Error(error.message);
  },

  // ─── MANAGED MEDIA COVERAGE ────────────────────────────────────────
  async getMediaCoverage(): Promise<ManagedMediaCoverage[]> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('managed_media_coverage')
      .select('*')
      .order('sort_order', { ascending: true });
    if (error) throw new Error(error.message);
    return (data as ManagedMediaCoverage[]) || [];
  },

  async createMediaCoverage(item: Omit<ManagedMediaCoverage, 'id' | 'created_at' | 'updated_at'>): Promise<ManagedMediaCoverage> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('managed_media_coverage')
      .insert([{ ...item, updated_at: new Date().toISOString() }])
      .select()
      .single();
    if (error) throw new Error(error.message);
    return data as ManagedMediaCoverage;
  },

  async updateMediaCoverage(id: string, updates: Partial<ManagedMediaCoverage>): Promise<void> {
    const supabase = createClient();
    const { error } = await supabase
      .from('managed_media_coverage')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', id);
    if (error) throw new Error(error.message);
  },

  async deleteMediaCoverage(id: string): Promise<void> {
    const supabase = createClient();
    const { error } = await supabase.from('managed_media_coverage').delete().eq('id', id);
    if (error) throw new Error(error.message);
  },

  // ─── MANAGED PRESS KIT ITEMS ───────────────────────────────────────
  async getPressKitItems(): Promise<ManagedPressKitItem[]> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('managed_press_kit_items')
      .select('*')
      .order('sort_order', { ascending: true });
    if (error) throw new Error(error.message);
    return (data as ManagedPressKitItem[]) || [];
  },

  async createPressKitItem(item: Omit<ManagedPressKitItem, 'id' | 'created_at' | 'updated_at'>): Promise<ManagedPressKitItem> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('managed_press_kit_items')
      .insert([{ ...item, updated_at: new Date().toISOString() }])
      .select()
      .single();
    if (error) throw new Error(error.message);
    return data as ManagedPressKitItem;
  },

  async updatePressKitItem(id: string, updates: Partial<ManagedPressKitItem>): Promise<void> {
    const supabase = createClient();
    const { error } = await supabase
      .from('managed_press_kit_items')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', id);
    if (error) throw new Error(error.message);
  },

  async deletePressKitItem(id: string): Promise<void> {
    const supabase = createClient();
    const { error } = await supabase.from('managed_press_kit_items').delete().eq('id', id);
    if (error) throw new Error(error.message);
  },

  // ─── APPLICATION STATUS UPDATES ────────────────────────────────────
  async updateVolunteerStatus(id: string, status: string): Promise<void> {
    const supabase = createClient();
    const { error } = await supabase
      .from('volunteer_applications')
      .update({ status })
      .eq('id', id);
    if (error) throw new Error(error.message);
  },

  async deleteVolunteer(id: string): Promise<void> {
    const supabase = createClient();
    const { error } = await supabase
      .from('volunteer_applications')
      .delete()
      .eq('id', id);
    if (error) throw new Error(error.message);
  },

  async updateAmbassadorStatus(id: string, status: string): Promise<void> {
    const supabase = createClient();
    const { error } = await supabase
      .from('ambassador_applications')
      .update({ status })
      .eq('id', id);
    if (error) throw new Error(error.message);
  },

  async deleteAmbassador(id: string): Promise<void> {
    const supabase = createClient();
    const { error } = await supabase
      .from('ambassador_applications')
      .delete()
      .eq('id', id);
    if (error) throw new Error(error.message);
  },

  async updatePartnershipStatus(id: string, status: string): Promise<void> {
    const supabase = createClient();
    const { error } = await supabase
      .from('partnership_inquiries')
      .update({ status })
      .eq('id', id);
    if (error) throw new Error(error.message);
  },

  async deletePartnership(id: string): Promise<void> {
    const supabase = createClient();
    const { error } = await supabase
      .from('partnership_inquiries')
      .delete()
      .eq('id', id);
    if (error) throw new Error(error.message);
  },

  async updateMemberStatus(id: string, status: string): Promise<void> {
    const supabase = createClient();
    const { error } = await supabase
      .from('membership_registrations')
      .update({ status })
      .eq('id', id);
    if (error) throw new Error(error.message);
  },

  async deleteMember(id: string): Promise<void> {
    const supabase = createClient();
    const { error } = await supabase
      .from('membership_registrations')
      .delete()
      .eq('id', id);
    if (error) throw new Error(error.message);
  },

  async deleteRegistration(id: string): Promise<void> {
    const supabase = createClient();
    const { error } = await supabase
      .from('movement_registrations')
      .delete()
      .eq('id', id);
    if (error) throw new Error(error.message);
  },

  async deleteNewsletterSubscriber(id: string): Promise<void> {
    const supabase = createClient();
    const { error } = await supabase
      .from('newsletter_signups')
      .delete()
      .eq('id', id);
    if (error) throw new Error(error.message);
  },

  async toggleNewsletterActive(id: string, is_active: boolean): Promise<void> {
    const supabase = createClient();
    const { error } = await supabase
      .from('newsletter_signups')
      .update({ is_active })
      .eq('id', id);
    if (error) throw new Error(error.message);
  },
};
